/* @flow */

import React from 'react'
import { format, parseISO } from 'date-fns'
import { Map } from 'immutable'
import { replaceWithObject } from './string'
import TranslationWithComponents from '../components/translation_with_components'
import type { ComponentsType } from '../components/translation_with_components'
import type { PathType } from '../data_handler'

export type PrefixReturnType = React$Element<any> | ?string
export type PrefixType = (path: PathType, obj?: Object, options?: OptionsType) => PrefixReturnType
export type OptionsType = { showError?: boolean, tagName?: string, components?: ComponentsType }
export type ReplaceObjectType = { [string]: string | number }
export type TranslationType = React$Element<any> | ?string

/**
 * Translation library
 * uses app/translations as import to get translations
 * exported as instance, so there will always only one instance exist
 */
class I18n {
  _locale: string
  _country: string
  _suffix: string
  _locales: Object
  _formatLocales: Object

  constructor() {
    this.locale = 'de-CH'
  }

  get locale(): string {
    return this._locale
  }

  get country(): string {
    return this._country
  }

  /**
   * full locale with country code
   * @return {string}
   */
  get localeCountry(): string {
    return `${this.locale}-${this.country}`
  }

  get suffix(): string {
    return this._suffix
  }

  /**
   * returns the selected translations
   * @return {Map} immutable Map
   * @private
   */
  get namespace(): Map<any, any> {
    return this._locales[this.locale]
  }

  /**
   * set locales
   * @param  {Object} locales { de: Immutable.Map() }
   * @return {null}
   */
  set locales(locales: Object): void {
    this._locales = locales
  }

  /**
   * set suffix can be used for products, will be added to the end and checked if it exists
   * @param  {string} suffix
   * @return {null}
   * @example
   *  i18n.suffix = 'redProduct'
   *  {
   *    de: {
   *      product: {
   *        redProduct: 'Red Product'
   *      }
   *    }
   *  }
   *  i18n.trans(['product']) == 'Red Product'
   */
  set suffix(suffix: string): void {
    this._suffix = suffix
  }

  /**
   * set locale
   * @param  {string} locale 'de' or 'de-ch'
   * @return {null}
   */
  set locale(locale: string): void {
    if (locale.length == 2) {
      this._locale = locale
    } else if (locale.length == 5) {
      this._locale = locale.substr(0, 2)
      this._country = locale.substr(3, 2)
    }
  }

  /**
   * fetch path in namespace and return null on error
   * @param  {array} path
   * @return {any}
   */
  getNamespace(path: PathType): any {
    if (this.namespace.hasIn(path)) {
      // $FlowFixMe
      return this.namespace.getIn(path)
    }
    return null
  }

  /**
   * use array for translations
   * @param {Array} path
   * @param {object} obj
   * @param {object} options
   * @returns {string|boolean}
   * @example
   * // simple
   * i18n.locales = {
   *   en: fromJS({
   *     owner: {
   *       firstName: 'Hans'
   *     }
   *   })
   * }
   * i18n.locale = 'en'
   * i18n.trans(['owner', 'firstName']) == 'Hans'
   * @example
   * // suffix
   * i18n.locales = {
   *   en: fromJS({
   *     title: {
   *       product1: 'My super duper first product',
   *       product2: 'is quite ok'
   *     },
   *     info: 'the info is the same'
   *   })
   * }
   * i18n.locale = 'en'
   * i18n.suffix = 'product2'
   * i18n.trans(['title']) == 'is quite ok'
   * i18n.trans(['text']) == 'the info is the same'
   * @example
   * // replace placeholders
   * i18n.locales = {
   *   en: fromJS({
   *     test: 'i work at ${workPlace}'
   *   })
   * }
   * i18n.trans(['test'], { workPlace: 'at-point ag' }) == 'i work at at-point ag'
   *
   * @example
   * // replace placehodlers with translations.
   * // i18n tries to translate placeholders which are not covered by object placeholders with translations
   * i18n.locales = {
   *   en: fromJS({
   *     title: 'i do own a ${card} creditcard',
   *     card: {
   *       amex: 'American Express'
   *     }
   *   })
   * }
   * i18n.locale = 'en'
   * i18n.suffix = 'amex'
   * i18n.trans(['title'], { card: 'xy' }) == 'i do own a xy creditcard'
   * i18n.trans(['title']) == 'i do own a American Express creditcard'
   */
  trans(path: PathType, obj: ReplaceObjectType = {}, options: OptionsType = {}): ?string {
    const showError: boolean = options.showError == undefined ? true : options.showError
    let value = null
    if (this.suffix) {
      const suffixedPath: PathType = (path.concat(this.suffix): any)
      value = this.getNamespace(suffixedPath)
    }
    if (value == null) value = this.getNamespace(path)

    if (value != null) {
      if (typeof value === 'string') {
        if (Object.keys(obj || {}).length > 0) {
          const replace = replaceWithObject(obj)
          value = replace(value)
        }
        return this.recursiveTranslations(value)
      }
      return value
    } else {
      return showError ? `Translation not found: ${path.join('.')}` : null
    }
  }

  recursiveTranslations(value: string) {
    return value.replace(/\${(.*?)}/gim, (_value, path: string, _pos, _fullText) => {
      const transPath = ((path.split('.'): any): PathType)
      return this.trans(transPath) || ''
    })
  }

  transTag(path: PathType, obj: ReplaceObjectType = {}, options: OptionsType = {}): React$Element<any> {
    const TagName = options.tagName || 'span'
    return <TagName dangerouslySetInnerHTML={{ __html: this.trans(path, obj, options) }} />
  }

  component(path: PathType, obj: ReplaceObjectType = {}, options: OptionsType = {}): React$Element<any> {
    return <TranslationWithComponents text={this.trans(path, obj, options)} components={options.components || {}} />
  }

  /**
   * create a function with prefixed translation
   * @param  {Array} arr
   * @return {function}
   * @example
   * {
   *   en: {
   *     test: {
   *       name: 'didum'
   *     }
   *   }
   * }
   * const trans = i18n.prefix(['test'])
   * trans(['name']) == 'didum'
   */
  prefix(arr: PathType, fn: string = 'trans', prefixOptions: OptionsType = {}): PrefixType {
    return (path: PathType, obj: ReplaceObjectType = {}, innerOptions: OptionsType = {}): PrefixReturnType => {
      const mergedOptions = Object.assign({}, prefixOptions, innerOptions)
      const prefixPath: PathType = (arr.concat(path): any)
      switch (fn) {
        case 'transTag':
          return this.transTag(prefixPath, obj, mergedOptions)
        case 'deep':
          return this.deep(prefixPath, obj, mergedOptions)
        case 'component':
          return this.component(prefixPath, obj, mergedOptions)
        default:
          return this.trans(prefixPath, obj, mergedOptions)
      }
    }
  }

  /**
   * search backwards in the path, used for labels
   * @param  {Array}  path
   * @param  {object}  [obj={}]
   * @param  {boolean} [showError=true]
   * @return {String|function}
   * @example
   * {
   *   labels: {
   *     street: 'Streetname'
   *   }
   * }
   * i18n.deep(['labels', 'customer', 'address', 'street']) == 'Streetname'
   * i18n.deep(['labels', 'customer', 'street']) == 'Streetname'
   * i18n.deep(['labels', 'street']) == 'Streetname'
   */
  deep(path: PathType, obj: ReplaceObjectType = {}, options: OptionsType = {}): ?string {
    const showError: boolean = options.showError == undefined ? true : options.showError
    let value = this.trans(path, obj, { showError: false })
    const pathCopy = (([].concat(path): any): PathType)
    while (value === null && pathCopy.length - 2 >= 0) {
      // $FlowFixMe
      pathCopy.splice(pathCopy.length - 2, 1)
      // $FlowFixMe
      value = this.trans(pathCopy, obj, { showError: false })
    }
    return value != null ? value : showError ? `Translation not found: ${path.join('.')}` : null
  }

  set formatLocales(locales: Object) {
    this._formatLocales = locales
  }

  format(date: Date | number | string, formatName: string = 'default'): string {
    const formatStr: ?string = this.trans(['date', formatName], {}, { showError: false })
    if (formatStr == null) throw new Error(`could not find date.${formatName}`)
    if (this._formatLocales[this.locale] == null) throw new Error(`could not find formatLocale for ${this.locale}`)
    if (typeof date == 'string') date = parseISO(date)
    return format(date, formatStr, { locale: this._formatLocales[this.locale] })
  }
}

export default new I18n()
